import { styled } from '@/src/stitches.config'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ICardLink } from '@/src/common/queries/FetchReceivedGiftDetails'
import StonlyContentModal from '@/src/common/components/marketing/modals/StonlyContentModal'
import { useEffect, useState } from 'react'
import Button from '@/src/common/components/marketing/buttons/Button'
import usePostDeactivateCardLink from '@/src/card-linking/hooks/usePostDeactivateCardLink'
import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'
import { ClipLoader } from 'react-spinners'
import {
    DISPATCH_CARD_LINK_MODAL,
    DISPATCH_HIDE_MODAL,
    DISPATCH_UNBRANDED_TO_LINK,
    DISPATCH_UNENROLL_MODAL,
} from '@/src/common/reducers/constants'
import AlertWithIcon from '@/src/common/components/elements/Alerts/AlertWithIcon'
import usePostDeleteCard from '@/src/card-linking/hooks/usePostDeleteCard'
import {
    CARD_LINK,
    UNBRANDED_TO_LINK,
    UNENROLL_CARD_FROM_LINK,
} from '@/src/modules/pg-plus/constants'
import CardLinkModalContent from '@/src/modules/pg-plus/redemption/modal-content/CardLinkModalContent'
import DispatchModal from '@/src/common/components/elements/Modals/DispatchModal'
import UnenrollCardModalContent from '@/src/modules/account/received/modals/UnenrollCardModalContent'

const DetailsWrapper = styled('div', {
    display: 'flex',
    ai: 'center',
    jc: 'center',
    p: '30px 15px',
    border: '3px solid $mediumGray',
    br: 10,
    gap: 15,
    my: 15,
    cursor: 'pointer',
    '.capitalize': {
        textTransform: 'capitalize',
    },
    '& p': {
        fontWeight: 'bold',
    },
})

const HoverDisplayWrapper = styled(DetailsWrapper, {
    '&:hover, :active': {
        borderColor: '$primary',
    },
})

const StyledFontAwesomeIcon = styled(FontAwesomeIcon, {
    color: '$primary',
    fontSize: 30,
})

const SmallText = styled('p', {
    textAlign: 'center',
    fontSize: '.75rem',
    lineHeight: '1rem',
    pt: 10,
    '& a': {
        color: '$primary',
        fontWeight: 'bold',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
})

const CardLinkInfo = ({
    card_network,
    link_status,
    last_four,
    brand_name,
    refetchGift,
    is_pg_plus = false,
}: ICardLink & { refetchGift: Function; is_pg_plus?: boolean }) => {
    const { dispatch, giftState, setSelection } = usePGPlusStepAndModalContext()
    const { showModal, modalName, cardUuid } = giftState

    const [showStonlyModal, setShowStonlyModal] = useState<boolean>(false)
    const [isActivating, setIsActivating] = useState<boolean>(false)
    const [cardIsLinked, setCardIsLinked] = useState<boolean>(false)
    const [deleteIsSuccess, setDeleteIsSuccess] = useState<boolean>(false)

    const isLinked: boolean = link_status === 'success'
    const isPending: boolean = link_status === 'pending'
    const isRemoved: boolean = link_status === 'removed'

    const {
        mutate: deactivateCard,
        isLoading: isDeactivateLoading,
        isError: isDeactivateError,
    } = usePostDeactivateCardLink()

    const deleteSavedCard = usePostDeleteCard()
    const {
        mutate: deleteCard,
        isSuccess: isDeleteSuccess,
        isError: isDeleteError,
        isLoading: isDeleteLoading,
    } = deleteSavedCard

    const statusText: string = isLinked
        ? 'Activated to'
        : isPending
        ? 'Pending to'
        : ''
    const network = !!card_network ? card_network : 'Debit or Credit Card'
    const hasLastFour = !!last_four

    useEffect(() => {
        setDeleteIsSuccess(isDeleteSuccess)
    }, [isDeleteSuccess])

    useEffect(() => {
        setCardIsLinked(!!link_status && !isRemoved)
    }, [isRemoved, link_status])

    useEffect(() => {
        cardIsLinked && setIsActivating(false)
    }, [cardIsLinked])

    if (cardIsLinked) {
        return (
            <>
                <StonlyContentModal
                    showModal={showStonlyModal}
                    setShowModal={setShowStonlyModal}
                />
                {modalName === UNENROLL_CARD_FROM_LINK && (
                    <DispatchModal
                        showModal={showModal}
                        hideModalAction={() => {
                            if (deleteIsSuccess) {
                                refetchGift()
                                setCardIsLinked(false)
                                setDeleteIsSuccess(false)
                            }
                            dispatch({ type: DISPATCH_HIDE_MODAL })
                        }}
                        testId={'unenroll-modal'}
                        width={'small'}
                    >
                        <UnenrollCardModalContent
                            cardNetwork={card_network}
                            cardLastFour={last_four}
                            dispatch={dispatch}
                            deleteCard={deleteCard}
                            disableButton={
                                isDeactivateLoading ||
                                isDeleteLoading ||
                                isActivating
                            }
                            isDeleteLoading={isDeleteLoading}
                            deleteIsSuccess={deleteIsSuccess}
                        />
                    </DispatchModal>
                )}
                <DetailsWrapper>
                    <StyledFontAwesomeIcon icon={faLink} />
                    <p>
                        {statusText}{' '}
                        <span className={!!card_network ? 'capitalize' : ''}>
                            {network}
                        </span>{' '}
                        {hasLastFour && `ending in ${last_four}`}
                    </p>
                </DetailsWrapper>
                <SmallText>
                    The next time you use your activated card to make a purchase
                    at {brand_name}, your bank account will automatically
                    credited the gift amount you spend until the gift is used in
                    full. Please allow 3 business days for the credit to appear
                    in your transaction history. Learn more about{' '}
                    <a onClick={() => setShowStonlyModal(true)}>how it works</a>
                    .
                </SmallText>
                <Button
                    label={'Deactivate'}
                    onClick={() => {
                        deactivateCard(
                            { card_uuid: cardUuid },
                            {
                                onSuccess: () => {
                                    refetchGift()
                                    setCardIsLinked(false)
                                },
                            }
                        )
                    }}
                    variant={'primary'}
                    textTransform={'uppercase'}
                    width={'full'}
                    size={'sm'}
                    data-testid={'unlink-button'}
                    style={{ marginTop: 15 }}
                    isLoading={isDeactivateLoading}
                    disabled={
                        isDeactivateLoading || isDeleteLoading || isActivating
                    }
                />
                <Button
                    label={'Delete Saved Card'}
                    onClick={() => {
                        dispatch({ type: DISPATCH_UNENROLL_MODAL, payload: {} })
                    }}
                    variant={'primaryOutline'}
                    textTransform={'uppercase'}
                    width={'full'}
                    data-testid={'unenroll-modal-button'}
                    style={{ marginTop: 10 }}
                    size={'sm'}
                    isLoading={isDeleteLoading}
                    disabled={
                        isDeactivateLoading || isDeleteLoading || isActivating
                    }
                />
                {(isDeactivateError || isDeleteError) && (
                    <AlertWithIcon iconName={'error'} style={{ marginTop: 15 }}>
                        <p>
                            Something went wrong. Please try again or click Chat
                            Now for assistance from our GiftGuide.
                        </p>
                    </AlertWithIcon>
                )}
            </>
        )
    }

    return (
        <>
            <StonlyContentModal
                showModal={showStonlyModal}
                setShowModal={setShowStonlyModal}
            />
            {modalName === CARD_LINK && (
                <DispatchModal
                    showModal={showModal}
                    hideModalAction={() =>
                        dispatch({ type: DISPATCH_HIDE_MODAL })
                    }
                    testId={'card-link-modal'}
                    variant={'iframe'}
                    width={'small'}
                    height={'550px'}
                >
                    <CardLinkModalContent
                        functions={{
                            setIsActivating: setIsActivating,
                            refetchGift: refetchGift,
                            handleStepChange: undefined,
                        }}
                        hideGiftDetailsButton={true}
                        deleteCard={deleteSavedCard}
                        cardToCardUuid={cardUuid}
                    />
                </DispatchModal>
            )}
            <HoverDisplayWrapper
                data-testid={'link-card-button'}
                onClick={() => {
                    if (!isActivating) {
                        is_pg_plus && setSelection(UNBRANDED_TO_LINK)
                        const nextStep: string = is_pg_plus
                            ? DISPATCH_UNBRANDED_TO_LINK
                            : DISPATCH_CARD_LINK_MODAL

                        dispatch({
                            type: nextStep,
                        })
                    }
                }}
            >
                {isActivating ? (
                    <ClipLoader size={30} />
                ) : (
                    <>
                        <StyledFontAwesomeIcon icon={faLink} />
                        <p>Activate your gift</p>
                    </>
                )}
            </HoverDisplayWrapper>
            <SmallText>
                Activate your gift to a credit or debit card to receive credit
                for your gift. The next time you make a purchase at {brand_name}
                , your bank account will automatically credited the gift amount
                you spend until the gift is used in full. Please allow 3
                business days for the credit to appear in your transaction
                history. Learn more about{' '}
                <a onClick={() => setShowStonlyModal(true)}>how it works</a>.
            </SmallText>
        </>
    )
}

export default CardLinkInfo
